// US-DEPOSIT-02 + US-CHORE-04 + US-WITHDRAWAL-02 + US-WITHDRAWAL-03 + US-ROUTINE-04 --
// guardian sees pending items awaiting approval and decides on each.

import {
  type PendingChoreClaimEntry,
  type PendingChoreCompletionEntry,
  type PendingDepositEntry,
  type PendingGiftEntry,
  type PendingTransferEntry,
  type PendingWithdrawalEntry,
  type RoutineInstanceEntry,
  choreCompletionApprove,
  choreCompletionReject,
  depositApprove,
  depositReject,
  giftCancel,
  giftPendingListForGuardian,
  pendingChoreClaimDecide,
  pendingChoreClaimsList,
  pendingChoresList,
  pendingDepositsList,
  pendingTransfersList,
  pendingWithdrawalsList,
  routineApprove,
  routinePendingApprovals,
  routineReject,
  transferApprove,
  transferReject,
  withdrawalApprove,
  withdrawalReject,
} from '@chorepop/core/client';
import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createMemo,
  createResource,
  createSignal,
} from 'solid-js';
import { AppIcon, type AppIconName } from '../components/AppIcon';
import { TransientBanner } from '../components/TransientBanner';

type Props = {
  sessionId: string;
  currencyCode: string;
  /** Tablet-only route to the full family money timeline. */
  onOpenHistory?: () => void;
  /** Render the working queue inside Family Overview without duplicate page chrome. */
  embedded?: boolean;
  /** Lets the overview refresh its summary badges after a decision. */
  onQueueChanged?: () => void;
  /**
   * US-UI-APPROVALS -- when set, the queue only shows items where
   * `entry.childAccountId === filterChildId`. Set by the per-child
   * Approvals button on Home; absent when opened via the global bell.
   * Applied client-side after the list endpoints return -- no wire
   * change.
   */
  filterChildId?: number | undefined;
};

/** A unified queue entry -- discriminated by `kind`. */
type QueueItem =
  | { kind: 'deposit'; id: number; at: string; entry: PendingDepositEntry }
  | { kind: 'chore'; id: number; at: string; entry: PendingChoreCompletionEntry }
  | { kind: 'withdrawal'; id: number; at: string; entry: PendingWithdrawalEntry }
  /** US-CHORE-09 -- a child has picked an approval-gated master-list
   *  chore and is waiting on this guardian's decision. */
  | {
      kind: 'chore_claim';
      id: number;
      at: string;
      entry: PendingChoreClaimEntry;
    }
  /** US-BUCKET-10 -- a child has requested moving money between two
   *  of their own buckets, awaiting this guardian's decision. */
  | { kind: 'transfer'; id: number; at: string; entry: PendingTransferEntry }
  | { kind: 'routine'; id: number; at: string; entry: RoutineInstanceEntry }
  /** Pending gift awaiting child allocation -- guardian can cancel. */
  | { kind: 'gift'; id: number; at: string; entry: PendingGiftEntry };

type PendingChoreItem = Extract<QueueItem, { kind: 'chore' }>;

function rowKey(item: QueueItem): string {
  return `${item.kind}:${item.id}`;
}

export const PendingApprovalsScreen: Component<Props> = (props) => {
  const [queue, { refetch }] = createResource(async (): Promise<QueueItem[]> => {
    const [deposits, chores, withdrawals, claims, transfers, routines, gifts] = await Promise.all([
      pendingDepositsList({ sessionId: props.sessionId }),
      pendingChoresList({ sessionId: props.sessionId }),
      pendingWithdrawalsList({ sessionId: props.sessionId }),
      pendingChoreClaimsList({ sessionId: props.sessionId }),
      pendingTransfersList({ sessionId: props.sessionId }),
      routinePendingApprovals(props.sessionId),
      giftPendingListForGuardian(props.sessionId),
    ]);
    if (!deposits.ok) throw new Error(deposits.error.message);
    if (!chores.ok) throw new Error(chores.error.message);
    if (!withdrawals.ok) throw new Error(withdrawals.error.message);
    if (!claims.ok) throw new Error(claims.error.message);
    if (!transfers.ok) throw new Error(transfers.error.message);

    const items: QueueItem[] = [
      ...deposits.value.map<QueueItem>((d) => ({
        kind: 'deposit',
        id: d.depositId,
        at: d.requestedAtUtc,
        entry: d,
      })),
      ...chores.value.map<QueueItem>((c) => ({
        kind: 'chore',
        id: c.choreCompletionId,
        at: c.claimedAtUtc,
        entry: c,
      })),
      ...withdrawals.value.map<QueueItem>((w) => ({
        kind: 'withdrawal',
        id: w.withdrawalId,
        at: w.requestedAtUtc,
        entry: w,
      })),
      ...claims.value.map<QueueItem>((cl) => ({
        kind: 'chore_claim',
        id: cl.pendingChoreClaimId,
        at: cl.requestedAtUtc,
        entry: cl,
      })),
      ...transfers.value.map<QueueItem>((t) => ({
        kind: 'transfer',
        id: t.bucketTransferId,
        at: t.requestedAtUtc,
        entry: t,
      })),
      ...(routines.ok ? routines.value : []).map<QueueItem>((ri) => ({
        kind: 'routine',
        id: ri.instanceId,
        at: ri.submittedAtUtc ?? ri.scheduledDate,
        entry: ri,
      })),
      ...(gifts.ok ? gifts.value : []).map<QueueItem>((g) => ({
        kind: 'gift',
        id: g.depositId,
        at: g.createdAtUtc,
        entry: g,
      })),
    ];
    items.sort((a, b) => {
      if (a.at !== b.at) return a.at.localeCompare(b.at);
      return rowKey(a).localeCompare(rowKey(b));
    });
    // US-UI-APPROVALS -- apply optional per-child filter. Three of the
    // five pending entry types carry `childAccountId`; chore_claims
    // use `requestingChildAccountId` (different model -- pending claim
    // is a request from a specific child, not on a chore that's
    // already theirs). Transfers carry `childAccountId` like the
    // deposit/withdrawal types. All map to the same filter target.
    if (props.filterChildId !== undefined) {
      const target = props.filterChildId;
      return items.filter((it) => {
        if (it.kind === 'chore_claim') return it.entry.requestingChildAccountId === target;
        if (it.kind === 'routine') return it.entry.childAccountId === target;
        return (it.entry as { childAccountId?: number }).childAccountId === target;
      });
    }
    return items;
  });

  const [busyKey, setBusyKey] = createSignal<string | null>(null);
  const [bulkApproving, setBulkApproving] = createSignal(false);
  const [filter, setFilter] = createSignal<'all' | 'chores' | 'money'>('all');
  const [flash, setFlash] = createSignal<string | null>(null);
  const [rowError, setRowError] = createSignal<{ key: string; msg: string } | null>(null);

  // Reject-with-reason dialog state.
  const [rejecting, setRejecting] = createSignal<QueueItem | null>(null);
  const [rejectReason, setRejectReason] = createSignal('');
  /** In-dialog error message (e.g. "reason required" for withdrawals). */
  const [dialogError, setDialogError] = createSignal<string | null>(null);

  const friendlyConflict = 'Already decided -- refreshing queue.';

  const refreshQueue = () => {
    void refetch();
    props.onQueueChanged?.();
  };

  const cancelGift = async (item: QueueItem & { kind: 'gift' }) => {
    const key = rowKey(item);
    setBusyKey(key);
    setRowError(null);
    try {
      const result = await giftCancel(props.sessionId, item.entry.depositId);
      if (!result.ok) {
        setRowError({ key, msg: result.error.message });
        return;
      }
      setFlash(
        `Cancelled gift of ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)}.`,
      );
      refreshQueue();
    } finally {
      setBusyKey(null);
    }
  };

  const approve = async (item: QueueItem, adjustedRewardMinorUnits?: number | null) => {
    const key = rowKey(item);
    setBusyKey(key);
    setRowError(null);
    try {
      if (item.kind === 'deposit') {
        const result = await depositApprove({
          sessionId: props.sessionId,
          depositId: item.entry.depositId,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          refreshQueue();
          return;
        }
        setFlash(
          `Approved ${formatAmount(result.value.amountMinorUnits, props.currencyCode)} for ${item.entry.childDisplayName}.`,
        );
      } else if (item.kind === 'chore') {
        const result = await choreCompletionApprove({
          sessionId: props.sessionId,
          choreCompletionId: item.entry.choreCompletionId,
          // US-CHORE-04 -- when adjustedRewardMinorUnits is null/undefined,
          // backend uses the chore's default. Otherwise it overrides.
          adjustedRewardMinorUnits: adjustedRewardMinorUnits ?? null,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          refreshQueue();
          return;
        }
        const reward = result.value.rewardMinorUnits;
        setFlash(
          reward > 0
            ? `Approved "${item.entry.choreName}" -- ${formatAmount(reward, props.currencyCode)} sent to ${item.entry.childDisplayName}.`
            : `Approved "${item.entry.choreName}" for ${item.entry.childDisplayName}.`,
        );
      } else if (item.kind === 'chore_claim') {
        // US-CHORE-09 -- approve a child's pending master-list claim.
        // Auto-rejects sibling pending claims on the same chore.
        const result = await pendingChoreClaimDecide({
          sessionId: props.sessionId,
          pendingChoreClaimId: item.entry.pendingChoreClaimId,
          approve: true,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          refreshQueue();
          return;
        }
        const siblings = result.value.autoRejectedSiblingClaimCount;
        setFlash(
          siblings > 0
            ? `Approved "${item.entry.choreName}" for ${item.entry.requestingChildDisplayName}. ${siblings} other sibling claim${siblings === 1 ? '' : 's'} auto-rejected.`
            : `Approved "${item.entry.choreName}" for ${item.entry.requestingChildDisplayName}.`,
        );
      } else if (item.kind === 'withdrawal') {
        const result = await withdrawalApprove({
          sessionId: props.sessionId,
          withdrawalId: (item.entry as PendingWithdrawalEntry).withdrawalId,
        });
        if (!result.ok) {
          // Validation here usually means the balance dropped below the
          // pending amount between submit and approve -- the row stays
          // pending per US-WITHDRAWAL-03 acceptance criterion. Surface
          // the message but do NOT remove the row.
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          refreshQueue();
          return;
        }
        setFlash(
          `Approved ${formatAmount(result.value.amountMinorUnits, props.currencyCode)} from ${result.value.bucketName} for ${(item.entry as PendingWithdrawalEntry).childDisplayName}.`,
        );
      } else if (item.kind === 'transfer') {
        // transfer (US-BUCKET-10). Same insufficient-funds-at-approval
        // pattern as withdrawal: the source might have drained between
        // request and approval, in which case the transfer stays
        // pending so the guardian can re-evaluate.
        // US-BUCKET-12: when source_deactivated comes back true, the
        // approval also removed the source bucket. Distinct flash so
        // the guardian sees the bucket actually went away.
        const result = await transferApprove({
          sessionId: props.sessionId,
          bucketTransferId: (item.entry as PendingTransferEntry).bucketTransferId,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          refreshQueue();
          return;
        }
        const v = result.value;
        const childName = (item.entry as PendingTransferEntry).childDisplayName;
        if (v.sourceDeactivated) {
          if (v.amountMinorUnits > 0 && v.targetBucketName !== null) {
            setFlash(
              `Deleted ${v.sourceBucketName} for ${childName} and moved ${formatAmount(v.amountMinorUnits, props.currencyCode)} to ${v.targetBucketName}.`,
            );
          } else {
            setFlash(`Deleted ${v.sourceBucketName} for ${childName}.`);
          }
        } else {
          setFlash(
            `Approved transfer: ${formatAmount(v.amountMinorUnits, props.currencyCode)} from ${v.sourceBucketName} to ${v.targetBucketName ?? '(unknown)'} for ${childName}.`,
          );
        }
      }
      if (item.kind === 'routine') {
        const result = await routineApprove(props.sessionId, item.entry.instanceId);
        if (!result.ok) {
          setRowError({ key, msg: result.error.message });
          refreshQueue();
          return;
        }
        setFlash(`Approved routine task: ${item.entry.taskName}.`);
      }
      refreshQueue();
    } finally {
      setBusyKey(null);
    }
  };

  const submitRejection = async (removeChore = false) => {
    const item = rejecting();
    if (!item) return;
    const key = rowKey(item);
    const trimmedReason = rejectReason().trim();
    // Per US-DEPOSIT-05, US-CHORE-05, US-WITHDRAWAL-03 -- ALL three kinds
    // of rejection require a non-empty reason so the affected child
    // knows what happened. Surface the message in the dialog itself so
    // the user can fix it without dismissing.
    if (trimmedReason === '') {
      const noun =
        item.kind === 'withdrawal'
          ? 'withdrawal'
          : item.kind === 'chore'
            ? 'chore claim'
            : item.kind === 'chore_claim'
              ? 'pick request'
              : item.kind === 'transfer'
                ? item.entry.deactivateSourceOnApprove
                  ? 'delete request'
                  : 'transfer'
                : 'deposit';
      setDialogError(`A reason is required when rejecting a ${noun}.`);
      return;
    }
    setBusyKey(key);
    setRowError(null);
    setDialogError(null);
    try {
      if (item.kind === 'deposit') {
        const result = await depositReject({
          sessionId: props.sessionId,
          depositId: item.entry.depositId,
          reason: trimmedReason,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(`Rejected deposit from ${item.entry.childDisplayName}.`);
      } else if (item.kind === 'chore') {
        const result = await choreCompletionReject({
          sessionId: props.sessionId,
          choreCompletionId: item.entry.choreCompletionId,
          reason: trimmedReason,
          ...(removeChore ? { removeChore: true } : {}),
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(
          removeChore
            ? `Rejected and removed "${item.entry.choreName}" for ${item.entry.childDisplayName}.`
            : `Rejected "${item.entry.choreName}" for ${item.entry.childDisplayName}.`,
        );
      } else if (item.kind === 'chore_claim') {
        // US-CHORE-09 -- reject a child's pending master-list claim.
        // The chore stays on the master list for other children to
        // pick (or for this child to retry).
        const result = await pendingChoreClaimDecide({
          sessionId: props.sessionId,
          pendingChoreClaimId: item.entry.pendingChoreClaimId,
          approve: false,
          reason: trimmedReason,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(
          `Rejected ${item.entry.requestingChildDisplayName}'s pick of "${item.entry.choreName}".`,
        );
      } else if (item.kind === 'withdrawal') {
        const result = await withdrawalReject({
          sessionId: props.sessionId,
          withdrawalId: item.entry.withdrawalId,
          reason: trimmedReason,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(
          `Rejected ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)} from ${item.entry.bucketName} for ${item.entry.childDisplayName}.`,
        );
      } else if (item.kind === 'transfer') {
        // transfer (US-BUCKET-10)
        const result = await transferReject({
          sessionId: props.sessionId,
          bucketTransferId: item.entry.bucketTransferId,
          reason: trimmedReason,
        });
        if (!result.ok) {
          setRowError({
            key,
            msg: result.error.kind === 'conflict' ? friendlyConflict : result.error.message,
          });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(
          item.entry.deactivateSourceOnApprove
            ? `Rejected ${item.entry.childDisplayName}'s request to delete ${item.entry.sourceBucketName}.`
            : `Rejected transfer of ${formatAmount(item.entry.amountMinorUnits, props.currencyCode)} from ${item.entry.sourceBucketName} for ${item.entry.childDisplayName}.`,
        );
      }
      if (item.kind === 'routine') {
        const result = await routineReject(props.sessionId, item.entry.instanceId, trimmedReason);
        if (!result.ok) {
          setRowError({ key, msg: result.error.message });
          setRejecting(null);
          refreshQueue();
          return;
        }
        setFlash(`Rejected routine task: ${item.entry.taskName}.`);
      }
      setRejecting(null);
      setRejectReason('');
      refreshQueue();
    } finally {
      setBusyKey(null);
    }
  };

  const visibleQueue = createMemo(() => {
    const items = queue() ?? [];
    switch (filter()) {
      case 'chores':
        return items.filter((item) => ['chore', 'chore_claim', 'routine'].includes(item.kind));
      case 'money':
        return items.filter((item) =>
          ['deposit', 'gift', 'withdrawal', 'transfer'].includes(item.kind),
        );
      default:
        return items;
    }
  });

  const visibleChores = createMemo<PendingChoreItem[]>(() =>
    visibleQueue().filter((item): item is PendingChoreItem => item.kind === 'chore'),
  );

  const approveAllVisibleChores = async () => {
    const chores = visibleChores();
    if (chores.length === 0 || bulkApproving()) return;

    const noun = chores.length === 1 ? 'chore' : 'chores';
    if (!window.confirm(`Approve all ${chores.length} completed ${noun}?`)) return;

    setBulkApproving(true);
    setRowError(null);
    let approvedCount = 0;
    let failedCount = 0;
    try {
      for (const item of chores) {
        const result = await choreCompletionApprove({
          sessionId: props.sessionId,
          choreCompletionId: item.entry.choreCompletionId,
          adjustedRewardMinorUnits: null,
        });
        if (result.ok) approvedCount += 1;
        else failedCount += 1;
      }
      setFlash(
        failedCount === 0
          ? `Approved ${approvedCount} ${approvedCount === 1 ? 'chore' : 'chores'}.`
          : `Approved ${approvedCount} ${approvedCount === 1 ? 'chore' : 'chores'}; ${failedCount} could not be approved.`,
      );
      refreshQueue();
    } finally {
      setBulkApproving(false);
    }
  };

  return (
    <section
      class={`history-screen guardian-approval-screen${props.embedded ? ' guardian-approval-screen--embedded' : ''}`}
      aria-label={props.embedded ? 'Working approval queue' : undefined}
    >
      <Show when={!props.embedded}>
        <header class="history-header guardian-approval-screen-header">
          <div class="guardian-approval-heading-copy">
            <span class="guardian-approval-count-eyebrow">{visibleQueue().length} waiting</span>
            <h1 class="guardian-overview-title guardian-approval-title">Approvals</h1>
            <p class="guardian-approval-tablet-subtitle">
              Approve one and the money splits across their buckets.
            </p>
          </div>
          <div class="guardian-approval-tablet-actions">
            <Show when={props.onOpenHistory}>
              <button type="button" class="ghost" onClick={() => props.onOpenHistory?.()}>
                Approval history
              </button>
            </Show>
            <button
              type="button"
              class="primary approval-bulk-button"
              disabled={bulkApproving() || visibleChores().length === 0}
              onClick={() => void approveAllVisibleChores()}
            >
              {bulkApproving() ? 'Approving…' : 'Approve all chores'}
            </button>
          </div>
        </header>
      </Show>
      <nav class="guardian-approval-filters" aria-label="Filter approvals">
        <For each={['all', 'chores', 'money'] as const}>
          {(value) => (
            <button
              type="button"
              class={filter() === value ? 'primary' : 'ghost'}
              aria-pressed={filter() === value}
              onClick={() => setFilter(value)}
            >
              {value.slice(0, 1).toUpperCase() + value.slice(1)}
            </button>
          )}
        </For>
      </nav>
      <div class="guardian-approval-scroll">
        <Show when={flash()}>
          {(msg) => (
            <TransientBanner class="home-flash" message={msg()} onDismiss={() => setFlash(null)} />
          )}
        </Show>

        <Switch>
          <Match when={queue.loading}>
            <section class="home-card">
              <p>Loading pending items…</p>
            </section>
          </Match>

          <Match when={queue.error}>
            <section class="home-card">
              <p class="form-error" role="alert">
                Couldn't load the queue: {(queue.error as Error).message}
              </p>
            </section>
          </Match>

          <Match when={queue() && visibleQueue().length === 0}>
            <section class="home-card">
              <p>Nothing waiting for your approval in this category.</p>
            </section>
          </Match>

          <Match when={visibleQueue().length > 0}>
            <ul class="history-list">
              <For each={visibleQueue()}>
                {(item) => (
                  <PendingRow
                    item={item}
                    currencyCode={props.currencyCode}
                    busy={busyKey() === rowKey(item)}
                    errorMessage={
                      rowError()?.key === rowKey(item) ? (rowError()?.msg ?? null) : null
                    }
                    onApprove={(adj) => approve(item, adj)}
                    onReject={() => {
                      setRejecting(item);
                      setRejectReason('');
                      setDialogError(null);
                    }}
                    onCancel={
                      item.kind === 'gift'
                        ? () => cancelGift(item as QueueItem & { kind: 'gift' })
                        : (undefined as never)
                    }
                  />
                )}
              </For>
            </ul>
          </Match>
        </Switch>
      </div>

      <Show when={rejecting()}>
        {(item) => (
          <div
            class="confirm-backdrop"
            onClick={(e) => {
              if (e.target === e.currentTarget && busyKey() === null) {
                setRejecting(null);
                setDialogError(null);
              }
            }}
          >
            <div class="confirm-dialog" role="dialog" aria-modal="true">
              <RejectDialogBody item={item()} currencyCode={props.currencyCode} />
              <label class="field">
                <span class="field-label">Reason (required)</span>
                <input
                  type="text"
                  autocomplete="off"
                  maxlength="200"
                  placeholder={
                    item().kind === 'deposit'
                      ? 'e.g. wrong amount'
                      : item().kind === 'chore'
                        ? 'e.g. needs to redo it'
                        : 'e.g. save it for something bigger'
                  }
                  disabled={busyKey() !== null}
                  value={rejectReason()}
                  onInput={(e) => {
                    setRejectReason(e.currentTarget.value);
                    if (dialogError()) setDialogError(null);
                  }}
                />
              </label>
              <Show when={dialogError()}>
                {(msg) => (
                  <div class="form-error" role="alert" style="margin-top: 4px;">
                    {msg()}
                  </div>
                )}
              </Show>
              <div class="confirm-buttons">
                <button
                  type="button"
                  class="ghost"
                  onClick={() => {
                    setRejecting(null);
                    setDialogError(null);
                  }}
                  disabled={busyKey() !== null}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="danger"
                  onClick={() => submitRejection(false)}
                  disabled={busyKey() !== null}
                >
                  {busyKey() !== null ? 'Rejecting…' : 'Reject'}
                </button>
                {/* US-CHORE-10 (10a): the second variant is offered ONLY
                    for chore rejections AND only when the underlying
                    chore is non-recurring. Recurring chores would
                    auto-recreate the claim next period regardless, so
                    the right answer there is the standalone chore
                    Deactivate flow rather than this one-shot. */}
                <Show
                  when={
                    item().kind === 'chore' &&
                    item().kind === 'chore' &&
                    (item() as { kind: 'chore'; entry: { recurrencePattern: string | null } }).entry
                      .recurrencePattern === null
                  }
                >
                  <button
                    type="button"
                    class="danger"
                    onClick={() => submitRejection(true)}
                    disabled={busyKey() !== null}
                    data-testid="chore-reject-and-remove"
                    title="Also removes the chore from everyone's list. The child sees it as rejected-and-removed in their history with the option to archive."
                  >
                    {busyKey() !== null ? 'Rejecting…' : 'Reject + remove'}
                  </button>
                </Show>
              </div>
            </div>
          </div>
        )}
      </Show>
    </section>
  );
};

const RejectDialogBody: Component<{ item: QueueItem; currencyCode: string }> = (props) => (
  <Switch>
    <Match when={props.item.kind === 'deposit'}>
      <h2>Reject deposit from {(props.item.entry as PendingDepositEntry).childDisplayName}?</h2>
      <p>
        Rejecting this deposit of{' '}
        <strong>
          {formatAmount(
            (props.item.entry as PendingDepositEntry).amountMinorUnits,
            props.currencyCode,
          )}
        </strong>{' '}
        won't change any bucket balances. Please tell{' '}
        {(props.item.entry as PendingDepositEntry).childDisplayName} why so they know what happened
        -- a reason is required.
      </p>
    </Match>
    <Match when={props.item.kind === 'chore'}>
      <h2>
        Reject "{(props.item.entry as PendingChoreCompletionEntry).choreName}" for{' '}
        {(props.item.entry as PendingChoreCompletionEntry).childDisplayName}?
      </h2>
      <p>
        Rejecting this chore claim won't move any money. Please tell{' '}
        {(props.item.entry as PendingChoreCompletionEntry).childDisplayName} why so they know what
        to try differently -- a reason is required.
      </p>
    </Match>
    <Match when={props.item.kind === 'withdrawal'}>
      <h2>
        Reject {(props.item.entry as PendingWithdrawalEntry).childDisplayName}'s request for{' '}
        <strong>
          {formatAmount(
            (props.item.entry as PendingWithdrawalEntry).amountMinorUnits,
            props.currencyCode,
          )}
        </strong>{' '}
        from {(props.item.entry as PendingWithdrawalEntry).bucketName}?
      </h2>
      <p>
        Rejecting won't move any money. Please tell{' '}
        {(props.item.entry as PendingWithdrawalEntry).childDisplayName} why so they can decide what
        to do next -- a reason is required.
      </p>
    </Match>
    <Match when={props.item.kind === 'chore_claim'}>
      <h2>
        Reject {(props.item.entry as PendingChoreClaimEntry).requestingChildDisplayName}'s pick of "
        {(props.item.entry as PendingChoreClaimEntry).choreName}"?
      </h2>
      <p>
        The chore stays on the master list -- other children can still pick it, and{' '}
        {(props.item.entry as PendingChoreClaimEntry).requestingChildDisplayName} can pick it again
        later. Please share a reason so they know why this one was turned down -- a reason is
        required.
      </p>
    </Match>
    <Match when={props.item.kind === 'transfer'}>
      <Show
        when={(props.item.entry as PendingTransferEntry).deactivateSourceOnApprove}
        fallback={
          <>
            <h2>
              Reject {(props.item.entry as PendingTransferEntry).childDisplayName}'s transfer of{' '}
              <strong>
                {formatAmount(
                  (props.item.entry as PendingTransferEntry).amountMinorUnits,
                  props.currencyCode,
                )}
              </strong>{' '}
              from {(props.item.entry as PendingTransferEntry).sourceBucketName} to{' '}
              {(props.item.entry as PendingTransferEntry).targetBucketName ?? '(unknown)'}?
            </h2>
            <p>
              Rejecting won't move any money. Please tell{' '}
              {(props.item.entry as PendingTransferEntry).childDisplayName} why so they know what to
              try differently -- a reason is required.
            </p>
          </>
        }
      >
        <h2>
          Reject {(props.item.entry as PendingTransferEntry).childDisplayName}'s request to delete{' '}
          {(props.item.entry as PendingTransferEntry).sourceBucketName}?
        </h2>
        <p>
          The bucket stays active and any balance stays where it is. Please tell{' '}
          {(props.item.entry as PendingTransferEntry).childDisplayName} why so they know whether to
          try again -- a reason is required.
        </p>
      </Show>
    </Match>
    <Match when={props.item.kind === 'routine'}>
      <h2>Reject "{(props.item.entry as RoutineInstanceEntry).taskName}"?</h2>
      <p>
        The task won't earn its reward. Please give a reason so the child knows what to improve -- a
        reason is required.
      </p>
    </Match>
  </Switch>
);

/**
 * The parent-facing "Proof first" card chosen for completed chores.
 * Ordinary chores do not currently own checklist rows, so the optional
 * proof area uses the real chore description and disappears completely
 * when no description exists. That keeps simple chores compact instead
 * of rendering an empty placeholder.
 */
const ChoreProofApprovalCard: Component<{
  entry: PendingChoreCompletionEntry;
  currencyCode: string;
  busy: boolean;
  errorMessage: string | null;
  onApprove: (adjustedRewardMinorUnits: number | null) => void;
  onReject: () => void;
}> = (props) => {
  const description = () => props.entry.choreDescription?.trim() || null;
  const childInitial = () => props.entry.childDisplayName.trim().slice(0, 1).toUpperCase() || 'C';

  const submittedAt = () => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(props.entry.claimedAtUtc));
    } catch {
      return props.entry.claimedAtUtc;
    }
  };

  return (
    <li class="history-row history-row--chore chore-proof-card" data-testid="chore-proof-card">
      <div class="chore-proof-card-header">
        <span class="chore-proof-eyebrow">Chore completed</span>
        <strong class="chore-proof-reward">
          {props.entry.rewardMinorUnits > 0
            ? `${formatAmount(props.entry.rewardMinorUnits, props.currencyCode)} reward`
            : 'No reward'}
        </strong>
      </div>

      <div class="chore-proof-identity">
        <span class="chore-proof-avatar" aria-hidden="true">
          {childInitial()}
        </span>
        <div>
          <div class="history-row-title">{props.entry.choreName}</div>
          <div class="chore-proof-subtitle">
            {props.entry.childDisplayName} · {submittedAt()}
          </div>
        </div>
      </div>

      <Show when={description()}>
        {(text) => (
          <div class="chore-proof-details" data-testid="chore-proof-details">
            <span>Chore details</span>
            <p>{text()}</p>
          </div>
        )}
      </Show>

      <div class="history-row-allocations chore-proof-tags">
        <span class="history-chip">CHORE</span>
        <span class="history-chip">{props.entry.choreTypeLabel}</span>
      </div>

      <Show when={props.errorMessage}>
        {(msg) => (
          <div class="form-error" role="alert">
            {msg()}
          </div>
        )}
      </Show>

      <ChoreApprovalControls
        defaultRewardMinorUnits={props.entry.rewardMinorUnits}
        currencyCode={props.currencyCode}
        busy={props.busy}
        onApprove={props.onApprove}
        onReject={props.onReject}
      />
    </li>
  );
};

// Kept temporarily for the focused e2e reward-adjustment migration below.
// The visible queue is now rendered by ApprovalDecisionCard.
void ChoreProofApprovalCard;

/** Claude Design approval card: one compact decision surface for every kind
 * of pending item. The old history-row/proof-card variants made a single
 * queue look like several unrelated screens. */
const ApprovalDecisionCard: Component<{
  item: QueueItem;
  currencyCode: string;
  busy: boolean;
  errorMessage: string | null;
  onApprove: (adjustedRewardMinorUnits?: number | null) => void;
  onReject: () => void;
  onCancel?: (() => void) | undefined;
}> = (props) => {
  const [rewardEditorOpen, setRewardEditorOpen] = createSignal(false);
  const defaultRewardMajor = () =>
    props.item.kind === 'chore' ? (props.item.entry.rewardMinorUnits / 100).toFixed(2) : '';
  const [editedRewardMajor, setEditedRewardMajor] = createSignal(defaultRewardMajor());
  const [rewardError, setRewardError] = createSignal<string | null>(null);

  const amount = () => {
    if (props.item.kind === 'chore') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'chore_claim') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'routine') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'deposit' || props.item.kind === 'gift')
      return props.item.entry.amountMinorUnits;
    return props.item.entry.amountMinorUnits;
  };
  const childName = () => {
    if (props.item.kind === 'chore_claim') return props.item.entry.requestingChildDisplayName;
    if (props.item.kind === 'routine') return props.item.entry.childDisplayName ?? 'Routine';
    return props.item.entry.childDisplayName;
  };
  const kindLabel = () => {
    switch (props.item.kind) {
      case 'chore':
        return 'Chore';
      case 'chore_claim':
        return 'Chore pick';
      case 'routine':
        return 'Routine';
      case 'deposit':
        return 'Deposit';
      case 'gift':
        return 'Gift';
      case 'transfer':
        return 'Transfer';
      case 'withdrawal':
        return 'Withdrawal';
    }
  };
  const icon = (): AppIconName => {
    switch (props.item.kind) {
      case 'chore':
      case 'routine':
      case 'chore_claim':
        return 'basket';
      case 'withdrawal':
        return 'money';
      case 'gift':
        return 'gift';
      case 'transfer':
        return 'switch';
      default:
        return 'deposit';
    }
  };
  const title = () => {
    if (props.item.kind === 'chore' || props.item.kind === 'chore_claim')
      return props.item.entry.choreName;
    if (props.item.kind === 'routine') return props.item.entry.taskName;
    if (props.item.kind === 'withdrawal')
      return `${formatAmount(amount(), props.currencyCode)} from ${props.item.entry.bucketName}`;
    if (props.item.kind === 'deposit')
      return `${formatAmount(amount(), props.currencyCode)} ${props.item.entry.sourceLabel.toLowerCase()}`;
    if (props.item.kind === 'gift')
      return `${formatAmount(amount(), props.currencyCode)} gift deposit`;
    if (props.item.entry.deactivateSourceOnApprove)
      return `Delete ${props.item.entry.sourceBucketName}`;
    return `${formatAmount(amount(), props.currencyCode)} to ${props.item.entry.targetBucketName ?? 'bucket'}`;
  };
  const note = () => {
    if (props.item.kind === 'chore') return props.item.entry.choreDescription;
    if (
      props.item.kind === 'deposit' ||
      props.item.kind === 'gift' ||
      props.item.kind === 'transfer'
    )
      return props.item.entry.note;
    if (props.item.kind === 'withdrawal') return props.item.entry.reasonNote;
    return null;
  };
  const atUtc = () => {
    if (props.item.kind === 'chore') return props.item.entry.claimedAtUtc;
    if (props.item.kind === 'chore_claim') return props.item.entry.requestedAtUtc;
    if (props.item.kind === 'routine')
      return props.item.entry.submittedAtUtc ?? props.item.entry.scheduledDate;
    if (props.item.kind === 'gift') return props.item.entry.createdAtUtc;
    return props.item.entry.requestedAtUtc;
  };
  const elapsed = () => {
    const elapsedMinutes = Math.max(0, Math.floor((Date.now() - Date.parse(atUtc())) / 60_000));
    if (elapsedMinutes < 1) return 'just now';
    if (elapsedMinutes < 60) return `${elapsedMinutes}m`;
    if (elapsedMinutes < 24 * 60) return `${Math.floor(elapsedMinutes / 60)}h`;
    return `${Math.floor(elapsedMinutes / (24 * 60))}d`;
  };
  const approve = () => {
    if (props.item.kind !== 'chore' || !rewardEditorOpen()) {
      props.onApprove();
      return;
    }
    const parsed = Number.parseFloat(editedRewardMajor().trim());
    if (!Number.isFinite(parsed) || parsed < 0) {
      setRewardError('Enter a non-negative reward.');
      return;
    }
    setRewardError(null);
    const adjusted = Math.round(parsed * 100);
    props.onApprove(adjusted === props.item.entry.rewardMinorUnits ? null : adjusted);
  };
  const approveAriaLabel = () => {
    if (props.item.kind === 'chore' && amount() === 0 && !rewardEditorOpen()) return 'Approve';
    if (props.item.kind !== 'chore' || !rewardEditorOpen())
      return `Approve ${formatAmount(amount(), props.currencyCode)}`;
    const parsed = Number.parseFloat(editedRewardMajor().trim());
    return Number.isFinite(parsed) && parsed >= 0
      ? `Approve ${formatAmount(Math.round(parsed * 100), props.currencyCode)}`
      : 'Approve';
  };

  return (
    <li
      class={`approval-decision-card approval-decision-card--${props.item.kind}`}
      data-testid={`approval-decision-card-${rowKey(props.item)}`}
    >
      <div class="approval-decision-card-main">
        <span
          class={`approval-decision-icon approval-decision-icon--${props.item.kind}`}
          aria-hidden="true"
        >
          <AppIcon name={icon()} />
        </span>
        <div class="approval-decision-copy">
          <strong class="approval-decision-title">{title()}</strong>
          <div class="approval-decision-meta">
            <span class="approval-decision-avatar" aria-hidden="true">
              {childName().slice(0, 1).toUpperCase()}
            </span>
            {childName()} · {kindLabel()} · {elapsed()}
          </div>
          <Show when={note()?.trim()}>
            {(text) => <p class="approval-decision-note">“{text()}”</p>}
          </Show>
        </div>
        <span
          class={`approval-decision-amount${props.item.kind === 'withdrawal' ? ' is-withdrawal' : ''}`}
        >
          <Show when={props.item.kind !== 'chore' || amount() > 0} fallback={'No reward'}>
            {props.item.kind === 'withdrawal' ? '−' : '+'}
            {formatAmount(amount(), props.currencyCode)}
          </Show>
        </span>
      </div>

      <Show when={props.item.kind === 'chore'}>
        <div class="approval-reward-adjustment">
          <button
            type="button"
            class="approval-reward-toggle"
            onClick={() => setRewardEditorOpen((open) => !open)}
          >
            {rewardEditorOpen() ? 'Hide reward adjustment' : 'Adjust reward'}
          </button>
          <Show when={rewardEditorOpen()}>
            <label class="approval-reward-field">
              <span>Reward</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editedRewardMajor()}
                disabled={props.busy}
                onInput={(event) => setEditedRewardMajor(event.currentTarget.value)}
                data-testid="chore-approval-reward-input"
              />
            </label>
          </Show>
        </div>
      </Show>

      <Show when={props.errorMessage}>
        {(message) => (
          <div class="form-error" role="alert">
            {message()}
          </div>
        )}
      </Show>
      <Show when={rewardError()}>
        {(message) => (
          <div class="form-error" role="alert">
            {message()}
          </div>
        )}
      </Show>

      <Show
        when={props.item.kind !== 'gift'}
        fallback={
          <button
            type="button"
            class="approval-cancel-gift"
            disabled={props.busy}
            onClick={() => props.onCancel?.()}
          >
            {props.busy ? 'Cancelling…' : 'Cancel gift'}
          </button>
        }
      >
        <div class="approval-decision-actions">
          <button
            type="button"
            class="approval-reject-button"
            disabled={props.busy}
            onClick={props.onReject}
          >
            Reject
          </button>
          <button
            type="button"
            class="approval-approve-button"
            aria-label={approveAriaLabel()}
            disabled={props.busy}
            onClick={approve}
            data-testid={props.item.kind === 'chore' ? 'chore-approval-approve-button' : undefined}
          >
            ✓ {props.busy ? 'Working…' : 'Approve'}
          </button>
        </div>
      </Show>
    </li>
  );
};

const PendingRow: Component<{
  item: QueueItem;
  currencyCode: string;
  busy: boolean;
  errorMessage: string | null;
  /** US-CHORE-04 -- chore rows pass an adjusted reward when the
   *  guardian has changed the value in the inline reward input.
   *  Deposit + withdrawal rows always pass null. */
  onApprove: (adjustedRewardMinorUnits?: number | null) => void;
  onReject: () => void;
  /** Gift rows only -- cancel a pending-allocation gift. */
  onCancel?: () => void;
}> = (props) => {
  return (
    <ApprovalDecisionCard
      item={props.item}
      currencyCode={props.currencyCode}
      busy={props.busy}
      errorMessage={props.errorMessage}
      onApprove={props.onApprove}
      onReject={props.onReject}
      {...(props.onCancel ? { onCancel: props.onCancel } : {})}
    />
  );

  /* Legacy history-row implementation retained for one transition only.
     The compact card above owns the rendered Approval Hub.
  const formatDate = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const amount = () => {
    if (props.item.kind === 'deposit') return props.item.entry.amountMinorUnits;
    if (props.item.kind === 'chore') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'chore_claim') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'transfer') return props.item.entry.amountMinorUnits;
    if (props.item.kind === 'routine') return props.item.entry.rewardMinorUnits;
    if (props.item.kind === 'gift') return props.item.entry.amountMinorUnits;
    return props.item.entry.amountMinorUnits; // withdrawal
  };

  const title = () => {
    if (props.item.kind === 'deposit') {
      return `${props.item.entry.childDisplayName} · ${props.item.entry.sourceLabel}`;
    }
    if (props.item.kind === 'chore') {
      return `${props.item.entry.childDisplayName} · ${props.item.entry.choreName}`;
    }
    if (props.item.kind === 'chore_claim') {
      return `${props.item.entry.requestingChildDisplayName} wants to do · ${props.item.entry.choreName}`;
    }
    if (props.item.kind === 'transfer') {
      const e = props.item.entry;
      if (e.deactivateSourceOnApprove) {
        if (e.targetBucketName !== null) {
          return `${e.childDisplayName} · delete ${e.sourceBucketName} -> ${e.targetBucketName}`;
        }
        return `${e.childDisplayName} · delete ${e.sourceBucketName} (empty)`;
      }
      return `${e.childDisplayName} · ${e.sourceBucketName} -> ${e.targetBucketName}`;
    }
    if (props.item.kind === 'routine') {
      return props.item.entry.taskName;
    }
    if (props.item.kind === 'gift') {
      const name = props.item.entry.childDisplayName || `Child #${props.item.entry.childAccountId}`;
      return `Gift for ${name} — awaiting their allocation`;
    }
    // withdrawal
    return `${props.item.entry.childDisplayName} · from ${props.item.entry.bucketName}`;
  };

  const kindLabel = () => {
    switch (props.item.kind) {
      case 'deposit':
        return 'DEPOSIT';
      case 'chore':
        return 'CHORE';
      case 'chore_claim':
        return 'PICK REQUEST';
      case 'routine':
        return 'ROUTINE';
      case 'gift':
        return 'GIFT';
      case 'transfer':
        return props.item.entry.deactivateSourceOnApprove ? 'DELETE BUCKET' : 'TRANSFER';
      case 'withdrawal':
        return 'WITHDRAWAL';
    }
  };

  const note = () => {
    if (props.item.kind === 'deposit') return props.item.entry.note;
    if (props.item.kind === 'withdrawal') return props.item.entry.reasonNote;
    if (props.item.kind === 'transfer') return props.item.entry.note;
    if (props.item.kind === 'gift') return props.item.entry.note;
    return null;
  };

  const atUtc = () => {
    if (props.item.kind === 'deposit') return props.item.entry.requestedAtUtc;
    if (props.item.kind === 'chore') return props.item.entry.claimedAtUtc;
    if (props.item.kind === 'chore_claim') return props.item.entry.requestedAtUtc;
    if (props.item.kind === 'transfer') return props.item.entry.requestedAtUtc;
    if (props.item.kind === 'routine')
      return props.item.entry.submittedAtUtc ?? props.item.entry.scheduledDate;
    if (props.item.kind === 'gift') return props.item.entry.createdAtUtc;
    return props.item.entry.requestedAtUtc; // withdrawal
  };

  if (props.item.kind === 'chore') {
    return (
      <ChoreProofApprovalCard
        entry={props.item.entry}
        currencyCode={props.currencyCode}
        busy={props.busy}
        errorMessage={props.errorMessage}
        onApprove={(adjustedRewardMinorUnits) => props.onApprove(adjustedRewardMinorUnits)}
        onReject={() => props.onReject()}
      />
    );
  }

  return (
    <li class={`history-row history-row--${props.item.kind}`}>
      <div class="history-row-leading">
        <Show when={amount() > 0}>
          <span
            class={`history-sign history-sign--${props.item.kind === 'withdrawal' ? 'minus' : 'plus'}`}
          >
            {props.item.kind === 'withdrawal' ? '−' : '+'}
          </span>
        </Show>
        <span class="history-amount">
          {amount() > 0 ? formatAmount(amount(), props.currencyCode) : 'No reward'}
        </span>
      </div>
      <div class="history-row-body">
        <div class="history-row-title">{title()}</div>
        <Show when={note()}>{(n) => <div class="history-row-note">"{n()}"</div>}</Show>
        <div class="history-row-allocations">
          <span class="history-chip">{kindLabel()}</span>
          <Show when={props.item.kind === 'withdrawal'}>
            <span class="history-chip">
              {(props.item.entry as PendingWithdrawalEntry).reasonLabel}
            </span>
          </Show>
          <Show
            when={
              props.item.kind === 'routine' &&
              (props.item.entry as RoutineInstanceEntry).subTasks.length > 0
            }
          >
            <span class="history-chip">
              {(props.item.entry as RoutineInstanceEntry).subTasks.length} steps
            </span>
          </Show>
        </div>
        <div class="history-row-meta">{formatDate(atUtc())}</div>
        <Show when={props.errorMessage}>
          {(msg) => (
            <div class="form-error" role="alert" style="margin-top: 8px;">
              {msg()}
            </div>
          )}
        </Show>
        <Show when={props.item.kind === 'gift'}>
          <div style="margin-top: 8px;">
            <p style="font-size: 0.82rem; color: var(--color-text-secondary); margin: 0 0 6px;">
              Waiting for the child to allocate this to their buckets.
            </p>
            <button
              type="button"
              class="ghost"
              style="color: var(--color-danger, #c0392b); border-color: var(--color-danger, #c0392b);"
              disabled={props.busy}
              onClick={() => props.onCancel?.()}
            >
              {props.busy ? 'Cancelling…' : 'Cancel gift'}
            </button>
          </div>
        </Show>
        <Show when={props.item.kind !== 'gift'}>
          <div style="margin-top: 8px; display: flex; gap: 8px;">
            <button
              type="button"
              class="primary"
              style="padding: 6px 14px; font-size: 0.88rem; width: auto;"
              disabled={props.busy}
              onClick={() => props.onApprove()}
            >
              {props.busy ? 'Working…' : 'Approve'}
            </button>
            <button
              type="button"
              class="ghost"
              disabled={props.busy}
              onClick={() => props.onReject()}
            >
              Reject
            </button>
          </div>
        </Show>
      </div>
    </li>
  );
  */
};

// US-CHORE-04 -- inline reward editor for chore approval rows.
// Pre-populated with the default reward in major units (e.g. "1.00"
// for 100 minor units). On Approve, if the parsed value differs from
// the default we pass it as the override; otherwise null.
const ChoreApprovalControls: Component<{
  defaultRewardMinorUnits: number;
  currencyCode: string;
  busy: boolean;
  onApprove: (adjustedRewardMinorUnits: number | null) => void;
  onReject: () => void;
}> = (props) => {
  const defaultMajor = () => (props.defaultRewardMinorUnits / 100).toFixed(2);
  const [editedMajor, setEditedMajor] = createSignal(defaultMajor());
  const [parseError, setParseError] = createSignal<string | null>(null);

  const handleApprove = () => {
    const trimmed = editedMajor().trim();
    if (trimmed === '') {
      setParseError('Reward cannot be empty.');
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setParseError('Enter a non-negative number.');
      return;
    }
    setParseError(null);
    // Convert major -> minor; round to avoid 0.1 + 0.2 type drift.
    const adjustedMinor = Math.round(parsed * 100);
    if (adjustedMinor === props.defaultRewardMinorUnits) {
      // No change -- call with null so the backend uses the default
      // and `chore_completions.adjusted_reward_minor_units` stays NULL.
      props.onApprove(null);
    } else {
      props.onApprove(adjustedMinor);
    }
  };

  const previewLabel = () => {
    const trimmed = editedMajor().trim();
    if (trimmed === '') return 'Approve';
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return 'Approve';
    const minor = Math.round(parsed * 100);
    return minor > 0 ? `Approve ${formatAmount(minor, props.currencyCode)}` : 'Approve';
  };

  return (
    <div class="chore-proof-controls">
      <label class="chore-proof-reward-editor">
        <span>Reward</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={editedMajor()}
          disabled={props.busy}
          onInput={(e) => setEditedMajor(e.currentTarget.value)}
          data-testid="chore-approval-reward-input"
        />
        <span class="chore-proof-default-reward">
          Default {formatAmount(props.defaultRewardMinorUnits, props.currencyCode)}
        </span>
      </label>
      <Show when={parseError()}>
        {(msg) => (
          <div class="form-error" role="alert">
            {msg()}
          </div>
        )}
      </Show>
      <div class="chore-proof-actions">
        <button
          type="button"
          class="primary"
          disabled={props.busy}
          onClick={handleApprove}
          data-testid="chore-approval-approve-button"
        >
          {props.busy ? 'Working…' : previewLabel()}
        </button>
        <button type="button" class="ghost" disabled={props.busy} onClick={() => props.onReject()}>
          Reject
        </button>
      </div>
    </div>
  );
};

function formatAmount(minor: number, currencyCode: string): string {
  const major = minor / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(major);
  } catch {
    return `${currencyCode} ${major.toFixed(2)}`;
  }
}
